import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { message, Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { PrivilegeType, SDK_TOKEN_SCOPE } from '@/consts/team'
import { cn } from '@/utils/style'

import { decodeJwtExp } from './CliTokenComponent'

interface NewSdkTokenModalProps {
  onClose: () => void
}

export function NewSdkTokenModal({ onClose }: NewSdkTokenModalProps) {
  const { t } = useTranslation()
  const [scope, setScope] = useState<number>(SDK_TOKEN_SCOPE.AllInstances)
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(false)

  const handleGenerate = () => {
    setLoading(true)
    API.getPlatformToken({
      privilege_type: PrivilegeType.SdkAccess,
      sdk_token_scope: scope,
    })
      .then((res) => {
        if (res.token) {
          setToken(res.token)
        }
      })
      .finally(() => setLoading(false))
  }

  const tokenInfo = token ? decodeJwtExp(token) : null

  const handleCopy = () => {
    navigator.clipboard.writeText(token)
    message.success(t('profile.sdkToken.copied'))
  }

  const scopeOptions = [
    {
      value: SDK_TOKEN_SCOPE.AllInstances,
      label: t('profile.sdkToken.scopeAllLabel'),
      desc: t('profile.sdkToken.scopeAllDesc'),
    },
    {
      value: SDK_TOKEN_SCOPE.Restricted,
      label: t('profile.sdkToken.scopeRestrictedLabel'),
      desc: t('profile.sdkToken.scopeRestrictedDesc'),
    },
  ]

  return (
    <Modal className="w-[640px]" open={true} onClose={onClose}>
      <Modal.Header>{t('profile.sdkToken.modalTitle')}</Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="flex justify-center items-center min-h-[88px]">
            <Loading />
          </div>
        ) : token ? (
          <>
            <div className="border border-neutral-200 rounded-lg px-4 py-3 min-h-[88px] break-all t-sb text-primary">
              {token}
            </div>
            {tokenInfo && (
              <p className="mt-2 t-sm text-secondary">
                <span>{t('profile.sdkToken.modalWarning')}</span>
                <span className="text-highlight">
                  {t('profile.sdkToken.modalWarningHighlight', {
                    date: tokenInfo.expirationDate,
                    days: tokenInfo.daysRemaining,
                  })}
                </span>
                <span>{t('profile.sdkToken.modalWarningAfter')}</span>
              </p>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="t-sb text-primary">
              <Trans i18nKey="profile.sdkToken.scopeTitle">Token Scope</Trans>
            </div>
            {scopeOptions.map((option) => (
              <label
                key={option.value}
                className={cn(
                  'border rounded-lg px-4 py-3 cursor-pointer transition-colors',
                  scope === option.value
                    ? 'border-btn-secondary bg-neutral-50'
                    : 'border-neutral-200 hover:border-neutral-300',
                )}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="sdk-token-scope"
                    checked={scope === option.value}
                    onChange={() => setScope(option.value)}
                  />
                  <span className="t-sb text-primary">{option.label}</span>
                </div>
                <div className="mt-1 ml-5 t-sm text-secondary">{option.desc}</div>
              </label>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} className="min-w-[88px]">
          {t('profile.sdkToken.modalCancel')}
        </Button>
        {token ? (
          <Button type="secondary" onClick={handleCopy} className="min-w-[88px]">
            {t('profile.sdkToken.modalCopy')}
          </Button>
        ) : (
          <Button
            type="secondary"
            disabled={loading}
            onClick={handleGenerate}
            className="min-w-[88px]"
          >
            {t('profile.sdkToken.generateBtn')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
